import React, { useMemo } from 'react'
import { Select } from '../ui/Select'
import { useI18n } from '../../i18n'

const CATEGORIES = [
  'POLICY',
  'PROCEDURE',
  'REPORT',
  'LOG',
  'SCREENSHOT',
  'CONFIGURATION',
  'DIAGRAM',
  'METRIC',
  'EXPORT',
  'APPROVAL',
  'REGISTER',
  'OTHER',
] as const

export type ArtifactCategory = (typeof CATEGORIES)[number]

export function inferArtifactCategory(fileName: string): ArtifactCategory {
  const name = fileName.toLowerCase()
  if (/\.(png|jpe?g|gif|webp)$/.test(name)) return 'SCREENSHOT'
  if (/\.(csv|xlsx|json)$/.test(name)) {
    if (/register/.test(name)) return 'REGISTER'
    if (/report|review|assessment/.test(name)) return 'REPORT'
    if (/log/.test(name)) return 'LOG'
    return 'EXPORT'
  }
  if (/log/.test(name)) return 'LOG'
  if (/config(uration)?/.test(name)) return 'CONFIGURATION'
  if (/diagram|network/.test(name)) return 'DIAGRAM'
  if (/metric|dashboard|kpi/.test(name)) return 'METRIC'
  if (/policy/.test(name)) return 'POLICY'
  if (/procedure/.test(name)) return 'PROCEDURE'
  if (/approval/.test(name)) return 'APPROVAL'
  if (/register/.test(name)) return 'REGISTER'
  return 'OTHER'
}

type Props = {
  value: ArtifactCategory
  onChange: (value: ArtifactCategory) => void
  /** Show a non-binding suggestion from file name only (never overwrites value). */
  uploadFileName?: string
  required?: boolean
}

export function EvidenceCategorySelector({ value, onChange, uploadFileName, required }: Props) {
  const { t } = useI18n()

  const suggestedCategory = useMemo(() => {
    const file = String(uploadFileName || '').trim()
    if (!file) return null
    return inferArtifactCategory(file)
  }, [uploadFileName])

  const options = CATEGORIES.map((c) => ({
    value: c,
    label: t(`governance.evidenceArtifacts.category.${c}`, c),
  }))

  return (
    <div>
      <Select<ArtifactCategory>
        label={t('governance.evidenceArtifacts.artifactCategory', 'Artifact category')}
        required={required}
        options={options}
        value={value}
        onChange={(v) => {
          if (!v) return
          onChange(v)
        }}
      />
      {suggestedCategory && suggestedCategory !== value ? (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('governance.evidenceArtifacts.suggestedCategory', 'Suggested from file')}:{' '}
          {t(`governance.evidenceArtifacts.category.${suggestedCategory}`, suggestedCategory)}
        </p>
      ) : null}
    </div>
  )
}
